// GraphSearchBox — Ctrl+F in the graph editors.
//
// Type and get the nodes that match; click one (or ↑/↓ + Enter) and the editor
// goes there and the box closes. Paste a coordinate — "3700, 2600" — and it
// jumps to that point instead, same close.
//
// It searches the LIVE graph module refs, not the saved blueprint, so a node
// you added a minute ago is findable. And it searches one level INTO containers
// (graph groups, state machines, event functions), because that is precisely
// where a node you cannot find has gone: the editor's own left panel lists only
// top-level event nodes, so anything inside a group was invisible to every
// search path the app had.
//
// Results carry their location in words ("Event graph › Orbit Camera Rig"),
// never coordinates. A coordinate is what people were being handed instead of
// an answer.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { findGraphNodes, parseCoordinate, describeLocation } from '../lib/graphNodeSearch'
import { requestFocus, FOCUS_REASONS } from '../lib/graphFocus'
// One reader, shared with the add-node palette's "IN THIS GRAPH" section — the
// two must never disagree about what is in the graph.
import { readGraphs, SURFACE_TO_GRAPH } from '../lib/liveGraphs'

const GRAPH_BADGE = { body: 'BODY', face: 'FACE', event: 'EVENT' }

export function GraphSearchBox({ surface, onClose }) {
  const preferGraph = SURFACE_TO_GRAPH[surface] ?? null
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const [note, setNote] = useState(null)
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select() }, [])

  const coord = useMemo(() => parseCoordinate(query), [query])
  // Re-read the live refs on every keystroke: they are module singletons the
  // editor mutates in place, so there is nothing to subscribe to and a memo on
  // `query` alone is exactly right — cheap (a few hundred nodes) and never stale.
  const results = useMemo(
    () => (coord ? [] : findGraphNodes(readGraphs(), query, { preferGraph })),
    [query, coord, preferGraph],
  )

  useEffect(() => { setCursor(0) }, [query])

  const go = useCallback(async (req) => {
    setNote(null)
    const result = await requestFocus(req)
    if (result.ok) { onClose?.(); return }
    // Superseded means the user asked for something else in the meantime —
    // that is not a failure to report at them.
    if (result.reason === FOCUS_REASONS.superseded) return
    setNote(result.reason === FOCUS_REASONS.notFound
      ? 'Could not reach it — the editor did not open that graph.'
      : 'Focus cancelled.')
  }, [onClose])

  const submit = useCallback(() => {
    if (coord) {
      if (!preferGraph) { setNote('Open a graph editor first.'); return }
      go({ graph: preferGraph, point: coord, label: `${coord.x}, ${coord.y}` })
      return
    }
    const m = results[cursor]
    if (m) go({ graph: m.graph, nodeId: m.nodeId, container: m.container, label: m.label })
  }, [coord, preferGraph, results, cursor, go])

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose?.(); return }
    if (e.key === 'Enter') { e.preventDefault(); submit(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); return }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)) }
  }

  return (
    <div
      data-testid="graph-search"
      // Stop keystrokes reaching the canvas: Delete deletes nodes there, and
      // typing "Delete" into a search box must never edit the graph.
      onKeyDown={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
        zIndex: 500, width: 460, maxWidth: 'calc(100% - 24px)',
        background: 'var(--m-panel)', border: '1px solid var(--m-line-hi)',
        borderRadius: 6, boxShadow: '0 8px 28px rgba(0,0,0,0.7)',
        fontFamily: 'var(--m-font)', overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: results.length || coord || note ? '1px solid var(--m-line)' : 'none' }}>
        <span style={{ color: 'var(--m-fg-3)', fontSize: 11 }}>🔍</span>
        <input
          ref={inputRef}
          data-testid="graph-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Find a node — or paste 3700, 2600"
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            color: 'var(--m-fg)', fontSize: 12, fontFamily: 'var(--m-font)',
          }}
        />
        <button
          type="button"
          data-testid="graph-search-close"
          onClick={onClose}
          title="Close (Esc)"
          style={{
            background: 'transparent', border: '1px solid var(--m-line-hi)', color: 'var(--m-fg-2)',
            borderRadius: 4, padding: '1px 6px', fontSize: 10, cursor: 'pointer',
          }}
        >✕</button>
      </div>

      {coord && (
        <div
          data-testid="graph-search-coord"
          onClick={submit}
          style={{ padding: '8px 12px', cursor: 'pointer', color: 'var(--m-fg-2)', fontSize: 11, display: 'flex', gap: 8, alignItems: 'center' }}
        >
          <span style={{ color: 'var(--m-accent)' }}>⊕</span>
          <span>Go to <b style={{ color: 'var(--m-fg)' }}>{coord.x}, {coord.y}</b> in the {surface === 'events' ? 'event' : surface} graph</span>
          <span style={{ marginLeft: 'auto', color: 'var(--m-fg-3)', fontSize: 10 }}>Enter</span>
        </div>
      )}

      {!coord && query.trim() !== '' && results.length === 0 && (
        <div data-testid="graph-search-empty" style={{ padding: '8px 12px', color: 'var(--m-fg-3)', fontSize: 11, fontStyle: 'italic' }}>
          No node matches “{query.trim()}”.
        </div>
      )}

      {!coord && results.length > 0 && (
        <div data-testid="graph-search-results" style={{ maxHeight: 260, overflowY: 'auto' }}>
          {results.map((m, i) => (
            <div
              key={`${m.graph}:${m.container?.id ?? ''}:${m.nodeId}`}
              data-testid="graph-search-result"
              data-node-id={m.nodeId}
              data-graph={m.graph}
              onMouseEnter={() => setCursor(i)}
              onClick={() => go({ graph: m.graph, nodeId: m.nodeId, container: m.container, label: m.label })}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', cursor: 'pointer',
                background: i === cursor ? 'var(--m-panel-hi)' : 'transparent',
                borderBottom: '1px solid var(--m-line)',
              }}
            >
              <span style={{
                fontSize: 8, letterSpacing: '.1em', color: 'var(--m-fg-3)',
                border: '1px solid var(--m-line-hi)', borderRadius: 3, padding: '1px 4px', flexShrink: 0,
              }}>{GRAPH_BADGE[m.graph]}</span>
              <span style={{ color: 'var(--m-fg)', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {m.label}
              </span>
              <span style={{ color: 'var(--m-fg-3)', fontSize: 10, flexShrink: 0 }}>{m.type}</span>
              <span style={{ marginLeft: 'auto', color: 'var(--m-fg-3)', fontSize: 10, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {describeLocation(m)}
              </span>
            </div>
          ))}
        </div>
      )}

      {note && (
        <div data-testid="graph-search-note" style={{ padding: '6px 12px', color: 'var(--m-warn)', fontSize: 10 }}>{note}</div>
      )}
    </div>
  )
}

export default GraphSearchBox
